import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { Close as CloseIcon, Upload } from '@mui/icons-material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import apiService from '../../services/api';
import FormTextField, { RequiredLabel } from '../common/FormTextField';

interface guestDialogProps {
  open: boolean;
  guest: any | null;
  onClose: () => void;
  onSave: () => void;
}

export interface guestForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  image: File | null;
}

const GuestEditDialog: React.FC<guestDialogProps> = ({
  open,
  guest,
  onClose,
  onSave,
}) => {
  const ImageUploadWrapper = styled(Box)(({ theme }) => ({
    width: 140,
    height: 140,
    borderRadius: 8,
    border: '1px solid #ccc',
    overflow: 'hidden',
    position: 'relative',
    cursor: 'pointer',
    transition: '0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&:hover .hoverLayer': {
      opacity: 1,
    },

    '.emptyBox': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      color: '#777',
      fontSize: 13,
    },

    '.previewImg': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    '.hoverLayer': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: '0.3s',
    },
  }));

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    setError,
    clearErrors,
  } = useForm<guestForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      image: null,
    },
  });

  useEffect(() => {
    if (guest && open) {
      reset({
        firstName: guest.firstName || '',
        lastName: guest.lastName || '',
        email: guest.email || '',
        phone: guest.phone || '',
        image: null,
      });
      setPreviewImage(guest.profiImgUrl || null);
    } else if (!guest && open) {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        image: null,
      });

      setPreviewImage(null);
    }
  }, [guest, open, reset]);

  ////////////////// image upload //////////////////
  const handleUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      setError('image', {
        type: 'manual',
        message: 'Only JPG, JPEG, and PNG files are allowed',
      });
      setPreviewImage(null);
      setValue('image', null);
      e.target.value = '';
      return;
    }

    clearErrors('image');

    const previewUrl = URL.createObjectURL(file);
    setPreviewImage(previewUrl);
    setValue('image', file, { shouldValidate: true });

    e.target.value = '';
  };

  const onSubmit = async (data: guestForm) => {
    setLoading(true);
    // setError('');

    try {
      const formData = new FormData();

      formData.append('firstName', data.firstName.trim() || '');
      formData.append('lastName', data.lastName.trim() || '');
      formData.append('email', data.email.trim() || '');
      formData.append('phone', data.phone.trim() || '');

      if (data.image instanceof File) {
        formData.append('img', data.image);
      } else if (guest?.profiImgUrl) {
        formData.append('img', guest.profiImgUrl);
      }

      const isEditing = !!guest;

      if (isEditing) {
        await apiService.updateGuest(guest.id, formData);
      } else {
        await apiService.createGuest(formData);
      }

      toast.success(`Guest ${isEditing ? 'updated' : 'created'} successfully`);

      onSave();
      onClose();
    } catch (err: any) {
      // setError(err?.response?.data?.message || 'Something went wrong');
      toast.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {guest ? 'Edit Guest Details' : 'Create Guest Details'}
        <IconButton onClick={onClose} sx={{ float: 'right' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {/* {error && <Alert severity="error">{error}</Alert>} */}

          <Grid container spacing={3}>
            {/* image */}
            <Grid item xs={12} display="flex" justifyContent="center">
              <Box
                sx={{
                  width: 140,
                  textAlign: 'center',
                }}
              >
                {/* Image Upload Box */}
                <ImageUploadWrapper
                  onClick={() =>
                    document.getElementById('guest-upload')?.click()
                  }
                >
                  {previewImage ? (
                    <>
                      <img src={previewImage} className="previewImg" />

                      {/* Hover Layer */}
                      <Box className="hoverLayer">
                        <CameraAltIcon sx={{ color: '#fff', fontSize: 32 }} />
                      </Box>
                    </>
                  ) : (
                    <Box className="emptyBox">
                      <Upload sx={{ fontSize: 32 }} />
                      Upload Guest Photo
                    </Box>
                  )}
                </ImageUploadWrapper>

                {/* Hidden Input */}
                <input
                  id="guest-upload"
                  type="file"
                  hidden
                  accept="image/*"
                  // {...register('image', {
                  //   required: 'Guest photo is required',
                  // })}
                  // onChange={e => handleUploadImage(e)}

                  onChange={handleUploadImage}
                />

                {/* Error Message */}
                {errors.image && (
                  <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>
                    {errors.image.message}
                  </Typography>
                )}
                {/* {errors.image && (
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      color: '#d32f2f',
                      fontSize: 13,
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      maxWidth: 260,
                      mx: 'auto',
                    }}
                  >
                    <Typography variant="caption">
                      {errors.image.message}
                    </Typography>
                  </Box>
                )} */}
              </Box>
            </Grid>

            {/* first name */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="firstName"
                control={control}
                label={<RequiredLabel label="First Name" />}
                fullWidth
                rules={{
                  required: 'First Name is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                  validate: (value: string) =>
                    value.trim().length > 0 || 'First Name is required',
                }}
                inputProps={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[0-9]/g, '');
                  },
                }}
              />
            </Grid>

            {/* last name */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="lastName"
                control={control}
                label={<RequiredLabel label="Last Name" />}
                fullWidth
                rules={{
                  required: 'Last Name is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                  validate: (value: string) =>
                    value.trim().length > 0 || 'First Name is required',
                }}
                inputProps={{
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[0-9]/g, '');
                  },
                }}
              />
            </Grid>

            {/* email */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                }}
                label={<RequiredLabel label="Email Id" />}
                fullWidth
              />
            </Grid>

            {/* phone */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="phone"
                control={control}
                rules={{
                  required: 'Contact Number is required',
                  pattern: {
                    value: /^\d{9,15}$/,
                    message:
                      'Contact number must contain only numbers (9–15 digits)',
                  },
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onKeyDown={e => {
                  if (
                    !/[0-9]/.test(e.key) &&
                    !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
                      e.key
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                label={<RequiredLabel label="Contact Number" />}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>

          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <CircularProgress size={16} />
            ) : guest ? (
              'Update Guest'
            ) : (
              'Create Guest'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GuestEditDialog;
